/**
 * Upload throughput math + formatters — pure, dependency-free, framework-free so
 * the UploadManager engine and any UI can share one source of truth for speed,
 * ETA, and the throughput graph. Kept a leaf module (no imports) to avoid cycles.
 */

/** Human-readable transfer rate, e.g. 1572864 → "1.5 MB/s". "—" if unknown. */
export function fmtSpeed(bytesPerSec) {
    if (!bytesPerSec || !Number.isFinite(bytesPerSec) || bytesPerSec < 1) return "—";
    const u = ["B/s", "KB/s", "MB/s", "GB/s"];
    const i = Math.min(u.length - 1, Math.floor(Math.log(bytesPerSec) / Math.log(1024)));
    return `${(bytesPerSec / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
}

/** A rate of generic items/sec, e.g. files/sec → "3.2 files/s". "—" if unknown. */
export function fmtRate(perSec, unit = "items") {
    if (!perSec || !Number.isFinite(perSec) || perSec <= 0) return "—";
    return `${perSec >= 10 ? Math.round(perSec) : perSec.toFixed(1)} ${unit}/s`;
}

/** Compact duration from ms, e.g. 90000 → "1m 30s", 3720000 → "1h 2m". */
export function fmtDuration(ms) {
    if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
    const totalSec = Math.round(ms / 1000);
    if (totalSec < 1) return "0s";
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

/** ETA formatter: Infinity / null / non-finite collapse to "—". */
export function fmtEta(ms) {
    if (ms == null || !Number.isFinite(ms)) return "—";
    return fmtDuration(ms);
}

/**
 * createRateTracker — smoothed throughput + ETA from cumulative-progress samples.
 *
 * Feed it the CUMULATIVE work done (bytes, or a file count) with the current
 * timestamp via `update(done, now)`; it returns the smoothed rate in units/sec.
 * An exponential moving average keeps a momentary stall (e.g. while a wave mints
 * signed URLs and no bytes move) or a burst from swinging the displayed speed.
 *
 * `now` is injected (never read internally) so the tracker stays pure and unit
 * tests are deterministic.
 */
export function createRateTracker({ alpha = 0.3 } = {}) {
    let lastDone = null;
    let lastT = null;
    let ema = null;

    return {
        /** Record a cumulative reading; returns the current smoothed rate. */
        update(done, now) {
            if (lastT == null) {
                lastDone = done;
                lastT = now;
                return ema ?? 0;
            }
            const dt = (now - lastT) / 1000;
            if (dt <= 0) return ema ?? 0;
            const inst = Math.max(0, (done - lastDone) / dt);
            ema = ema == null ? inst : ema * (1 - alpha) + inst * alpha;
            lastDone = done;
            lastT = now;
            return ema;
        },
        /** Latest smoothed rate (units/sec). */
        get rate() {
            return ema ?? 0;
        },
        /** Estimated ms to reach `total`. 0 if done, Infinity if stalled. */
        etaMs(done, total) {
            const remaining = total - done;
            if (remaining <= 0) return 0;
            if (!ema || ema <= 0) return Infinity;
            return (remaining / ema) * 1000;
        },
        reset() {
            lastDone = null;
            lastT = null;
            ema = null;
        },
    };
}

/**
 * Append a value to a fixed-length ring buffer, returning a NEW array (so React
 * state updates see a fresh reference). Oldest samples fall off the front.
 */
export function pushSample(samples, value, cap = 60) {
    const base = samples.length >= cap ? samples.slice(samples.length - cap + 1) : samples.slice();
    base.push(value);
    return base;
}
